import React, { useState, useEffect } from "react";
import { List, Card, Space, Typography, Table, Input, Button, Row, Col, Alert, Statistic, Divider } from "@pankod/refine-antd";
import { SearchOutlined, DatabaseOutlined, InfoCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { getBridgeStockBySku, getBridgeStockStats } from "Services/productServices";
import toast from "react-hot-toast";

const { Text, Title } = Typography;

export const StockQuery: React.FC = () => {
  const [sku, setSku] = useState("");
  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      const response = await getBridgeStockStats();
      setStats(response.data);
    } catch (err) {
      console.error("Failed to fetch bridge stock stats");
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleSearch = async () => {
    if (!sku.trim()) {
      toast.error("Please enter a SKU");
      return;
    }

    setLoading(true);
    setError(null);
    setStockData(null);
    
    try {
      const response = await getBridgeStockBySku(sku.trim());
      setStockData(response.data);
    } catch (err: any) {
      const msg = err.response?.data?.message || "Failed to fetch stock data. Ensure the SKU exists in the bridge global map.";
      setError(msg);
      toast.error("SKU not found in bridge cache");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Warehouse Code",
      dataIndex: "code",
      key: "code",
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: "Warehouse Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Available Qty",
      dataIndex: "availableQty",
      key: "availableQty",
      render: (qty: number) => (
        <Text type={qty > 0 ? "success" : "danger"} strong>
          {qty}
        </Text>
      )
    }
  ];

  return (
    <List title="Bridge Stock Lookup">
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card size="small">
            <Statistic 
              title="Items in Global Map" 
              value={stats?.itemCount || 0} 
              prefix={<DatabaseOutlined />} 
              suffix={stats?.exists ? "" : "(Not Indexed)"}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic 
              title="Cache Status" 
              value={stats?.exists ? "Active" : "Empty"} 
              valueStyle={{ color: stats?.exists ? '#52c41a' : '#cf1322' }}
              prefix={<InfoCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic 
              title="Cache TTL" 
              value={stats?.ttl ? Math.floor(stats.ttl / 60) : 0} 
              prefix={<ClockCircleOutlined />} 
              suffix="minutes"
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <Row gutter={16} align="bottom">
            <Col span={12}>
              <Text strong>Search SKU in Global Map</Text>
              <Input 
                placeholder="Enter SKU (e.g. 1001-311)" 
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                onPressEnter={handleSearch}
                prefix={<DatabaseOutlined />}
                size="large"
                style={{ marginTop: 8 }}
              />
            </Col>
            <Col span={6}>
              <Button 
                type="primary" 
                icon={<SearchOutlined />} 
                onClick={handleSearch} 
                loading={loading}
                size="large"
              >
                Query Stock
              </Button>
            </Col>
          </Row>

          <Divider />

          {error && (
            <Alert
              message="No Data Found"
              description={error}
              type="info"
              showIcon
              style={{ marginTop: 24 }}
            />
          )}

          {stockData && (
            <div style={{ marginTop: 24 }}>
              <Title level={4}>Stock Results for: {stockData.sku}</Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Last bridge update: {new Date(stockData.timestamp).toLocaleString()}
              </Text>
              <Table 
                dataSource={stockData.stock} 
                columns={columns} 
                rowKey="code"
                pagination={false}
              />
            </div>
          )}
        </Space>
      </Card>
      
      <Card style={{ marginTop: 24 }} size="small">
        <Text type="secondary">
          <DatabaseOutlined /> This data is retrieved directly from the **ProntoBridge Redis Global Map**. 
          It shows the current cached state of stock levels across all known warehouses for this SKU.
        </Text>
      </Card>
    </List>
  );
};
